import type { CatalogPermission, CatalogRole, IamCatalog } from '../../services/iam';

// Derived lookups shared across Access pages/primitives, built once per
// catalog fetch rather than re-scanning arrays every render.

export interface PermissionLookupEntry {
    readonly group: string;
    readonly wired: boolean;
    readonly routeCount: number;
}

export type PermissionLookup = ReadonlyMap<string, PermissionLookupEntry>;

export function buildPermissionLookup(permissions: readonly CatalogPermission[]): PermissionLookup {
    return new Map(
        permissions.map((p) => [
            p.id,
            { group: p.group, wired: p.routeCount > 0, routeCount: p.routeCount }
        ])
    );
}

export function buildRoleLookup(roles: readonly CatalogRole[]): ReadonlyMap<string, CatalogRole> {
    return new Map(roles.map((role) => [role.key, role]));
}

/** Strips the 'roles/' or 'roles/*.' prefix noise for compact display - keeps the full id in `title`. */
export function shortRoleKey(roleKey: string): string {
    return roleKey.replace(/^roles\//, '');
}

/** A role's human title, or null for bundles (no title) and unresolved keys. */
export function roleTitle(role: CatalogRole | undefined): string | null {
    return role && role.kind === 'role' ? role.title : null;
}

export function catalogLookups(catalog: IamCatalog | undefined) {
    return {
        permissionLookup: buildPermissionLookup(catalog?.permissions ?? []),
        roleLookup: buildRoleLookup(catalog?.roles ?? [])
    };
}
